import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCalendar, faEdit, faUser, faIdCard } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { BankInfo } from '../../types';
import { DisplayNameChoice } from '../../hooks/useDisplayNameEditor';

interface ProfileData {
    displayName: string;
    email?: string;
    createdAt?: number;
    lastAccess?: number;
    bankInfo?: BankInfo;
    isAnonymous: boolean;
    isOwner: boolean;
    userId: string;
}

interface DisplayNameEditor {
    isEditing: boolean;
    newDisplayName: string;
    error: string | null;
    saving: boolean;
    choice: DisplayNameChoice;
    setChoice: (choice: DisplayNameChoice) => void;
    canChooseScope: boolean;
    accountDisplayName: string;
    canReset: boolean;
    handleEdit: () => void;
    handleSave: () => void;
    handleCancel: () => void;
    handleChange: (value: string) => void;
    handleKeyPress: (e: React.KeyboardEvent) => void;
}

interface PermissionFlags {
    canEditDisplayName: boolean;
    canEditBankInfo: boolean;
    canViewProfile: boolean;
}

interface ProfileDetailsGridProps {
    displayData: ProfileData;
    permissions: PermissionFlags;
    displayNameEditor: DisplayNameEditor;
    isCurrentUserOwner: boolean;
    isViewingSelf: boolean;
}

const ProfileDetailsGrid: React.FC<ProfileDetailsGridProps> = ({
                                                                   displayData,
                                                                   permissions,
                                                                   displayNameEditor,
                                                                   isCurrentUserOwner,
                                                                   isViewingSelf
                                                               }) => {
    const { t } = useTranslation();

    const scopeChoices: { value: DisplayNameChoice; label: string; description: string }[] = [
        {
            value: 'event',
            label: t('profile.components.profileDetailsGrid.scope.event.label'),
            description: t('profile.components.profileDetailsGrid.scope.event.description')
        },
        {
            value: 'account',
            label: t('profile.components.profileDetailsGrid.scope.account.label'),
            description: t('profile.components.profileDetailsGrid.scope.account.description')
        },
        ...(displayNameEditor.canReset ? [{
            value: 'reset' as DisplayNameChoice,
            label: t('profile.components.profileDetailsGrid.scope.reset.label'),
            description: t('profile.components.profileDetailsGrid.scope.reset.description', {
                name: displayNameEditor.accountDisplayName
            })
        }] : [])
    ];

    return (
        <div className="space-y-8">
            {/* Display Name Section - Prominent if editable */}
            {permissions.canEditDisplayName && (
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                        <FontAwesomeIcon icon={faUser} className="mr-2 text-blue-600 dark:text-blue-400" />
                        {t('profile.components.profileDetailsGrid.displayName')}
                        {isCurrentUserOwner && !isViewingSelf && (
                            <span className="ml-2 text-sm text-blue-600 dark:text-blue-400 font-normal">
                                {t('profile.components.profileDetailsGrid.ownerEdit')}
                            </span>
                        )}
                    </h3>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        {displayNameEditor.isEditing ? (
                            <div className="space-y-4">
                                <input
                                    type="text"
                                    value={displayNameEditor.choice === 'reset'
                                        ? displayNameEditor.accountDisplayName
                                        : displayNameEditor.newDisplayName}
                                    onChange={(e) => displayNameEditor.handleChange(e.target.value)}
                                    onKeyPress={displayNameEditor.handleKeyPress}
                                    disabled={displayNameEditor.choice === 'reset'}
                                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white text-lg disabled:opacity-60"
                                    placeholder={t('profile.components.profileDetailsGrid.enterDisplayName')}
                                />

                                {/* Where the new name applies. Without this the only
                                    possible answer was "this event", and nobody could
                                    tell that from the form. */}
                                {displayNameEditor.canChooseScope && (
                                    <fieldset className="space-y-2">
                                        <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t('profile.components.profileDetailsGrid.scope.legend')}
                                        </legend>

                                        {scopeChoices.map(({ value, label, description }) => (
                                            <label
                                                key={value}
                                                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                                    displayNameEditor.choice === value
                                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                                        : 'border-gray-300 dark:border-gray-600 hover:border-blue-300'
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="display-name-scope"
                                                    value={value}
                                                    checked={displayNameEditor.choice === value}
                                                    onChange={() => displayNameEditor.setChoice(value)}
                                                    disabled={displayNameEditor.saving}
                                                    className="mt-1"
                                                />
                                                <span>
                                                    <span className="block text-sm font-medium text-gray-900 dark:text-white">
                                                        {label}
                                                    </span>
                                                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        {description}
                                                    </span>
                                                </span>
                                            </label>
                                        ))}
                                    </fieldset>
                                )}

                                <div className="flex items-center space-x-3">
                                    <button
                                        onClick={displayNameEditor.handleSave}
                                        disabled={displayNameEditor.saving}
                                        className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                                    >
                                        {displayNameEditor.saving
                                            ? t('profile.components.profileDetailsGrid.saving')
                                            : t('profile.components.profileDetailsGrid.saveChanges')}
                                    </button>
                                    <button
                                        onClick={displayNameEditor.handleCancel}
                                        disabled={displayNameEditor.saving}
                                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                                    >
                                        {t('profile.components.profileDetailsGrid.cancel')}
                                    </button>
                                </div>
                                {displayNameEditor.error && (
                                    <p className="text-red-600 dark:text-red-400 text-sm">
                                        {displayNameEditor.error}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center justify-between">
                                <span className="text-gray-900 dark:text-white font-medium text-lg">
                                    {displayData.displayName}
                                </span>
                                <button
                                    onClick={displayNameEditor.handleEdit}
                                    className="glass-btn-primary inline-flex items-center !px-4 !py-2 text-sm"
                                >
                                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                                    {t('profile.components.profileDetailsGrid.edit')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Profile Information Grid */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <FontAwesomeIcon icon={faIdCard} className="mr-2 text-blue-600 dark:text-blue-400" />
                    {t('profile.components.profileDetailsGrid.profileInformation')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {displayData.email && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                                <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                                {t('profile.components.profileDetailsGrid.emailAddress')}
                            </label>
                            <p className="text-gray-900 dark:text-white font-medium">
                                {displayData.email}
                            </p>
                        </div>
                    )}

                    {displayData.createdAt && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                                <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                                {displayData.isAnonymous
                                    ? t('profile.components.profileDetailsGrid.created')
                                    : t('profile.components.profileDetailsGrid.memberSince')
                                }
                            </label>
                            <p className="text-gray-900 dark:text-white font-medium">
                                {new Date(displayData.createdAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </p>
                        </div>
                    )}

                    {displayData.lastAccess && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                                <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                                {t('profile.components.profileDetailsGrid.lastAccess')}
                            </label>
                            <p className="text-gray-900 dark:text-white font-medium">
                                {new Date(displayData.lastAccess).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </p>
                        </div>
                    )}

                    {/* User ID - Always show for reference */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                            <FontAwesomeIcon icon={faIdCard} className="mr-2" />
                            {t('profile.components.profileDetailsGrid.userId')}
                        </label>
                        <p className="text-gray-900 dark:text-white font-mono text-sm">
                            {displayData.userId}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileDetailsGrid;